import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  AdaptiveDpr,
  OrbitControls,
  Text,
} from '@react-three/drei';

import {
  Canvas,
  useFrame,
  useThree,
} from '@react-three/fiber';

import * as THREE from 'three';

import {
  SHARON_LEVEL_OPTIONS,
  SHARON_S6_MODEL,
} from './sharonS6Model';

import {
  LEVEL1_RACKS,
  LEVEL2_RACKS,
  RACK_TYPE_META,
  SHARON_RACK_COUNTS,
} from './sharonS6RackData';


/* =========================================================
   CONSTANTS
========================================================= */

const DARK = '#020617';

const PANEL =
  'linear-gradient(145deg, rgba(6,18,30,.84), rgba(3,9,18,.68))';

const PANEL_BORDER =
  '1px solid rgba(103,232,249,.24)';

const RACK = Object.freeze({
  width: 1.65,
  depth: 2.18,
  height: 6.45,
});

const HALL_CENTER_Z = 9;

const POD_CENTERS = [
  -27,
  -9,
  9,
  27,
];

const ROW_XS =
  POD_CENTERS.flatMap(
    (center) => [
      center - 2.15,
      center + 2.15,
    ]
  );


const RACK_TYPE_ORDER = [
  'gpu',
  'leaf',
  'spine',
  'oob',
  'control',
  'storage',
  'storageOob',
  'spareLeaf',
];


const RACK_DISPLAY_LABELS = {
  gpu: 'Compute',
  leaf: 'Leaf',
  spine: 'Spine',
  oob: 'OOB',
  control: 'Control',
  storage: 'Storage',
  storageOob: 'Storage OOB',
  spareLeaf: 'Spare Leaf',
};


const LEVEL_TILE_LABELS = {
  level1: {
    title: 'L1',
    subtitle: 'DH102',
  },

  level2: {
    title: 'L2',
    subtitle: 'DH202',
  },

  stacked: {
    title: 'ALL',
    subtitle: 'L1 + L2',
  },
};


const CAMERA_LIMITS =
  Object.freeze({
    minX: -125,
    maxX: 125,

    minY: 1.2,
    maxY: 125,

    minZ: -115,
    maxZ: 115,

    minTargetY: 0,
    maxTargetY: 65,
  });


const summaryStyle = {
  background: PANEL,

  border:
    PANEL_BORDER,

  boxShadow:
    'inset 0 1px 0 rgba(255,255,255,.10), 0 24px 54px rgba(0,0,0,.34)',

  backdropFilter:
    'blur(24px) saturate(1.45)',

  WebkitBackdropFilter:
    'blur(24px) saturate(1.45)',
};


/* =========================================================
   BASIC HELPERS
========================================================= */

function rangePositions(
  count,
  spacing,
  center = 0
) {
  const start =
    center -
    ((count - 1) * spacing) /
      2;

  return Array.from(
    {
      length: count,
    },

    (_, index) =>
      start +
      index * spacing
  );
}


/* =========================================================
   HORIZONTAL FLOOR LABEL
========================================================= */

function HorizontalLabel({
  children,
  position,
  size = 1,
  color = '#ccfbf1',
  maxWidth = 24,
}) {
  return (
    <Text
      position={
        position
      }

      rotation={[
        -Math.PI / 2,
        0,
        0,
      ]}

      fontSize={
        size
      }

      color={
        color
      }

      anchorX="center"
      anchorY="middle"

      maxWidth={
        maxWidth
      }

      textAlign="center"
    >
      {children}
    </Text>
  );
}


/* =========================================================
   WALL
========================================================= */

function Wall({
  x,
  z,
  width,
  depth,
  yOffset,

  height = 3.1,

  color =
    '#79b8b5',

  opacity =
    0.72,
}) {
  return (
    <mesh
      position={[
        x,

        yOffset +
          height / 2 +
          0.08,

        z,
      ]}

      castShadow
      receiveShadow
    >
      <boxGeometry
        args={[
          width,
          height,
          depth,
        ]}
      />

      <meshStandardMaterial
        color={
          color
        }

        transparent={
          opacity < 1
        }

        opacity={
          opacity
        }

        roughness={
          0.66
        }

        metalness={
          0.08
        }

        emissive="#0e3a3b"

        emissiveIntensity={
          0.035
        }
      />
    </mesh>
  );
}


/* =========================================================
   ROOM OUTLINE
========================================================= */

function RoomOutline({
  x,
  z,
  width,
  depth,
  yOffset,

  color =
    '#79b8b5',

  opacity =
    0.7,
}) {
  const t =
    0.34;

  return (
    <group>
      <Wall
        x={x}

        z={
          z -
          depth / 2
        }

        width={
          width
        }

        depth={t}

        yOffset={
          yOffset
        }

        color={
          color
        }

        opacity={
          opacity
        }
      />


      <Wall
        x={x}

        z={
          z +
          depth / 2
        }

        width={
          width
        }

        depth={t}

        yOffset={
          yOffset
        }

        color={
          color
        }

        opacity={
          opacity
        }
      />


      <Wall
        x={
          x -
          width / 2
        }

        z={z}

        width={t}

        depth={
          depth
        }

        yOffset={
          yOffset
        }

        color={
          color
        }

        opacity={
          opacity
        }
      />


      <Wall
        x={
          x +
          width / 2
        }

        z={z}

        width={t}

        depth={
          depth
        }

        yOffset={
          yOffset
        }

        color={
          color
        }

        opacity={
          opacity
        }
      />
    </group>
  );
}


/* =========================================================
   FLOOR ZONE
========================================================= */

function FloorZone({
  center,
  size,
  yOffset,
  color,
  label,

  labelColor =
    '#dffafe',

  opacity =
    0.28,
}) {
  return (
    <group>
      <mesh
        position={[
          center[0],

          yOffset +
            0.02,

          center[1],
        ]}

        receiveShadow
      >
        <boxGeometry
          args={[
            size[0],
            0.16,
            size[1],
          ]}
        />

        <meshStandardMaterial
          color={
            color
          }

          transparent

          opacity={
            opacity
          }

          roughness={
            0.9
          }

          emissive={
            color
          }

          emissiveIntensity={
            0.03
          }
        />
      </mesh>


      {label && (
        <HorizontalLabel
          position={[
            center[0],

            yOffset +
              0.14,

            center[1],
          ]}

          size={
            0.95
          }

          color={
            labelColor
          }
        >
          {label}
        </HorizontalLabel>
      )}
    </group>
  );
}


/* =========================================================
   BUILDING SHELL
========================================================= */

function BuildingShell({
  yOffset,
  label,
}) {
  const {
    width,
    depth,
  } =
    SHARON_S6_MODEL
      .building;


  /*
   * Corrected structural columns.
   *
   * The previous version generated
   * a rectangular X × Z matrix and
   * put columns through the rack aisles.
   *
   * These positions place the columns
   * between the four Sharon HAC/rack bays.
   */
  const columnsX = [
    -36,
    -18,
    0,
    18,
    36,
  ];


  /*
   * H / J structural grid positions
   * through the active data hall.
   */
  const columnsZ = [
    2.7,
    15.3,
  ];


  return (
    <group>

      {/* FLOOR SLAB */}

      <mesh
        position={[
          0,
          yOffset - 0.24,
          0,
        ]}
        receiveShadow
      >
        <boxGeometry
          args={[
            width,
            0.48,
            depth,
          ]}
        />

        <meshStandardMaterial
          color="#213d47"
          roughness={0.56}
          metalness={0.12}
          emissive="#0d2730"
          emissiveIntensity={0.08}
        />
      </mesh>


      {/* OUTER WALLS */}

      <Wall
        x={0}
        z={-depth / 2}
        width={width}
        depth={0.72}
        yOffset={yOffset}
        height={4.2}
        opacity={0.78}
      />


      <Wall
        x={0}
        z={depth / 2}
        width={width}
        depth={0.72}
        yOffset={yOffset}
        height={4.2}
        opacity={0.78}
      />


      <Wall
        x={-width / 2}
        z={0}
        width={0.72}
        depth={depth}
        yOffset={yOffset}
        height={4.2}
        opacity={0.78}
      />


      <Wall
        x={width / 2}
        z={0}
        width={0.72}
        depth={depth}
        yOffset={yOffset}
        height={4.2}
        opacity={0.78}
      />


      {/* CORRECTED STRUCTURAL COLUMNS */}

      {columnsZ.flatMap(
        (z) =>
          columnsX.map(
            (x) => (
              <mesh
                key={`${label}-column-${x}-${z}`}
                position={[
                  x,
                  yOffset + 3.8,
                  z,
                ]}
                castShadow
                receiveShadow
              >
                <boxGeometry
                  args={[
                    0.72,
                    7.6,
                    0.72,
                  ]}
                />

                <meshStandardMaterial
                  color="#64748b"
                  roughness={0.72}
                  metalness={0.06}
                />
              </mesh>
            )
          )
      )}


      {/* LEVEL LABEL */}

      <HorizontalLabel
        position={[
          -41.5,
          yOffset + 0.16,
          31.5,
        ]}
        size={1.2}
        color="#67e8f9"
      >
        {label}
      </HorizontalLabel>

    </group>
  );
}


/* =========================================================
   RACK CABINET
========================================================= */

function RackCabinet({
  rack,
  yOffset,
  selected,
  onSelect,
  onHover,
}) {


  if (
    !rack.occupied
  ) {
    return (
      <group

        position={[
          rack.x,
          yOffset,
          rack.z,
        ]}
      >
        <mesh
          position={[
            0,
            0.16,
            0,
          ]}
        >
          <boxGeometry
            args={[
              RACK.width +
                0.12,

              0.12,

              RACK.depth +
                0.12,
            ]}
          />

          <meshBasicMaterial
            color="#64748b"

            wireframe

            transparent

            opacity={
              0.5
            }
          />
        </mesh>


        <HorizontalLabel
          position={[
            0,
            0.22,
            0,
          ]}

          size={
            0.28
          }

          color="#64748b"
        >
          EMPTY
        </HorizontalLabel>
      </group>
    );
  }


  const meta =
    RACK_TYPE_META[
      rack.type
    ] ||
    RACK_TYPE_META
      .gpu;


  const isGpu =
    rack.type ===
    'gpu';


  const height =
    isGpu
      ? RACK.height +
        0.35
      : RACK.height;


  return (
    <group
      position={[
        rack.x,
        yOffset,
        rack.z,
      ]}

      onClick={(
        event
      ) => {
        event.stopPropagation();

        onSelect?.(
          rack
        );
      }}

      onPointerOver={(
        event
      ) => {
        event.stopPropagation();

        onHover?.(
          rack
        );

        document.body
          .style.cursor =
          'pointer';
      }}

      onPointerOut={(
        event
      ) => {
        event.stopPropagation();

        onHover?.(
          null
        );

        document.body
          .style.cursor =
          'default';
      }}
    >
      <mesh
        position={[
          0,

          height / 2 +
            0.22,

          0,
        ]}

        castShadow
        receiveShadow
      >
        <boxGeometry
          args={[
            RACK.width,
            height,
            RACK.depth,
          ]}
        />

        <meshStandardMaterial
          color={
            selected
              ? '#e2f9ff'
              : '#0b1720'
          }

          roughness={
            0.4
          }

          metalness={
            0.72
          }

          emissive={
            meta.color
          }

          emissiveIntensity={
            selected
              ? 0.34
              : 0.12
          }
        />
      </mesh>


      <mesh
        position={[
          0,
          0.13,
          0,
        ]}

        castShadow
      >
        <boxGeometry
          args={[
            RACK.width +
              0.13,

            0.2,

            RACK.depth +
              0.13,
          ]}
        />

        <meshStandardMaterial
          color="#02070b"

          roughness={
            0.28
          }

          metalness={
            0.84
          }
        />
      </mesh>


      <mesh
        position={[
          0,

          height +
            0.3,

          0,
        ]}

        castShadow
      >
        <boxGeometry
          args={[
            RACK.width +
              0.08,

            0.16,

            RACK.depth +
              0.08,
          ]}
        />

        <meshStandardMaterial
          color={
            meta.accent
          }

          roughness={
            0.3
          }

          metalness={
            0.78
          }

          emissive={
            meta.color
          }

          emissiveIntensity={
            0.32
          }
        />
      </mesh>


      <mesh
        position={[
          0,

          height / 2 +
            0.22,

          RACK.depth / 2 +
            0.045,
        ]}
      >
        <boxGeometry
          args={[
            RACK.width *
              0.82,

            height *
              0.82,

            0.08,
          ]}
        />

        <meshStandardMaterial
          color={
            meta.color
          }

          roughness={
            0.38
          }

          metalness={
            0.5
          }

          emissive={
            meta.color
          }

          emissiveIntensity={
            selected
              ? 0.42
              : 0.18
          }
        />
      </mesh>


      <HorizontalLabel
        position={[
          0,
          0.18,
          0,
        ]}

        size={
          0.25
        }

        color={
          meta.accent
        }
      >
        {meta.shortLabel}
      </HorizontalLabel>
    </group>
  );
}


/* =========================================================
   HAC POD
========================================================= */

function HacsPod({
  centerX,
  centerZ,
  yOffset,
  number,
}) {
  return (
    <group>
      <mesh
        position={[
          centerX,

          yOffset +
            3.75,

          centerZ,
        ]}
      >
        <boxGeometry
          args={[
            3.75,
            6.65,
            25,
          ]}
        />

        <meshStandardMaterial
          color="#0e7490"

          transparent

          opacity={
            0.09
          }

          roughness={
            0.22
          }

          metalness={
            0.2
          }
        />
      </mesh>


      <mesh
        position={[
          centerX,

          yOffset +
            7.15,

          centerZ,
        ]}
      >
        <boxGeometry
          args={[
            3.7,
            0.18,
            25.3,
          ]}
        />

        <meshStandardMaterial
          color="#8be9f6"

          emissive="#22d3ee"

          emissiveIntensity={
            0.35
          }

          transparent

          opacity={
            0.72
          }
        />
      </mesh>


      <HorizontalLabel
        position={[
          centerX,

          yOffset +
            0.18,

          centerZ,
        ]}

        size={
          0.48
        }

        color="#a5f3fc"
      >
        HAC {number}
      </HorizontalLabel>
    </group>
  );
}


/* =========================================================
   BASKET SEGMENT
========================================================= */

function BasketSegment({
  x,
  z,
  length,
  direction = 'x',
  y,

  color =
    '#d8fbff',

  width =
    0.72,

  tier =
    1,
}) {
  const isX =
    direction ===
    'x';


  const ribCount =
    Math.max(
      2,

      Math.floor(
        length /
          2.4
      )
    );


  const offsets =
    rangePositions(
      ribCount,

      length /
        Math.max(
          1,

          ribCount -
            1
        )
    );


  return (
    <group
      position={[
        x,
        y,
        z,
      ]}
    >
      {Array.from({
        length: tier,
      }).map(
        (
          _,
          t
        ) => (
          <group
            key={t}

            position={[
              0,

              t *
                0.68,

              0,
            ]}
          >
            <mesh>
              <boxGeometry
                args={
                  isX
                    ? [
                        length,
                        0.28,
                        width,
                      ]

                    : [
                        width,
                        0.28,
                        length,
                      ]
                }
              />

              <meshStandardMaterial
                color={
                  color
                }

                wireframe

                transparent

                opacity={
                  0.32
                }

                emissive={
                  color
                }

                emissiveIntensity={
                  0.18
                }
              />
            </mesh>


            {offsets.map(
              (
                offset,
                index
              ) => (
                <mesh
                  key={
                    index
                  }

                  position={
                    isX
                      ? [
                          offset,
                          -0.14,
                          0,
                        ]

                      : [
                          0,
                          -0.14,
                          offset,
                        ]
                  }
                >
                  <boxGeometry
                    args={
                      isX
                        ? [
                            0.06,
                            0.06,
                            width,
                          ]

                        : [
                            width,
                            0.06,
                            0.06,
                          ]
                    }
                  />

                  <meshBasicMaterial
                    color={
                      color
                    }

                    toneMapped={
                      false
                    }
                  />
                </mesh>
              )
            )}
          </group>
        )
      )}
    </group>
  );
}


/* =========================================================
   PRIMARY CONTAINMENT
========================================================= */

function PrimaryContainment({
  yOffset,

  centerZ =
    HALL_CENTER_Z,
}) {
  const y =
    yOffset +
    9.35;


  const northZ =
    centerZ -
    15;


  const southZ =
    centerZ +
    15;


  return (
    <group>
      <BasketSegment
        x={0}

        z={
          northZ
        }

        length={
          78
        }

        y={y}

        color="#d8fbff"

        width={
          0.82
        }
      />


      <BasketSegment
        x={0}

        z={
          southZ
        }

        length={
          78
        }

        y={y}

        color="#d8fbff"

        width={
          0.82
        }
      />


      {ROW_XS.map(
        (x) => (
          <BasketSegment
            key={x}

            x={x}

            z={
              centerZ
            }

            length={
              30
            }

            direction="z"

            y={
              y -
              0.6
            }

            color="#d8fbff"

            width={
              0.66
            }
          />
        )
      )}


      <BasketSegment
        x={0}

        z={
          northZ -
          4.5
        }

        length={
          82
        }

        y={
          y +
          0.6
        }

        color="#30343b"

        width={
          0.82
        }
      />
    </group>
  );
}


/* =========================================================
   CORE HOLES
========================================================= */

function RowCorePair({
  x,
  z,
  yOffset,
}) {
  return (
    <group>
      {[
        -0.25,
        0.25,
      ].map(
        (dx) => (
          <mesh
            key={dx}

            position={[
              x +
                dx,

              yOffset +
                0.05,

              z,
            ]}

            rotation={[
              -Math.PI /
                2,

              0,
              0,
            ]}
          >
            <cylinderGeometry
              args={[
                0.13,
                0.13,
                0.13,
                18,
              ]}
            />

            <meshStandardMaterial
              color="#020617"

              emissive="#f59e0b"

              emissiveIntensity={
                0.35
              }
            />
          </mesh>
        )
      )}
    </group>
  );
}


function RowCores({
  yOffset,
}) {
  return (
    <group>
      {ROW_XS.flatMap(
        (x) =>
          [
            -4.2,
            22.2,
          ].map(
            (z) => (
              <RowCorePair
                key={`${x}-${z}`}

                x={x}

                z={z}

                yOffset={
                  yOffset
                }
              />
            )
          )
      )}
    </group>
  );
}


/* =========================================================
   PENETRATION
========================================================= */

function Penetration({
  x,
  z,
  yOffset,

  label =
    '600 x 150',
}) {
  return (
    <group>
      <mesh
        position={[
          x,

          yOffset +
            0.05,

          z,
        ]}
      >
        <boxGeometry
          args={[
            1.9,
            0.1,
            0.65,
          ]}
        />

        <meshStandardMaterial
          color="#020617"

          emissive="#f97316"

          emissiveIntensity={
            0.22
          }
        />
      </mesh>


      <mesh
        position={[
          x,

          yOffset +
            0.1,

          z,
        ]}
      >
        <boxGeometry
          args={[
            2.1,
            0.08,
            0.86,
          ]}
        />

        <meshBasicMaterial
          color="#fb923c"

          wireframe

          toneMapped={
            false
          }
        />
      </mesh>


      <HorizontalLabel
        position={[
          x,

          yOffset +
            0.16,

          z +
            0.72,
        ]}

        size={
          0.34
        }

        color="#fdba74"
      >
        {label}
      </HorizontalLabel>
    </group>
  );
}


/* =========================================================
   FLOOR RACKS
========================================================= */

function FloorRacks({
  racks,
  yOffset,
  selectedRack,
  onRackSelect,
  onRackHover,
}) {
  return racks.map(
    (rack) => (
      <RackCabinet
        key={
          rack.id
        }

        rack={
          rack
        }

        yOffset={
          yOffset
        }

        selected={
          selectedRack
            ?.id ===
          rack.id
        }

        onSelect={
          onRackSelect
        }

        onHover={
          onRackHover
        }
      />
    )
  );
}


/* =========================================================
   LEVEL 1 ARCHITECTURE
========================================================= */

function OverheadServices({ yOffset }) {
  const ceilingY = yOffset + 11.2;

  return (
    <group>

      {/* LIGHT SERVICE CEILING GRID */}
      <mesh
        position={[
          0,
          ceilingY,
          HALL_CENTER_Z,
        ]}
      >
        <boxGeometry
          args={[
            78,
            0.12,
            42,
          ]}
        />

        <meshStandardMaterial
          color="#64748b"
          transparent
          opacity={0.08}
          roughness={0.7}
          metalness={0.2}
        />
      </mesh>


      {/* LONG OVERHEAD SERVICE DUCTS */}
      {[-25, 0, 25].map((x) => (
        <mesh
          key={`duct-${x}`}
          position={[
            x,
            ceilingY - 0.75,
            HALL_CENTER_Z,
          ]}
        >
          <boxGeometry
            args={[
              2.6,
              1.15,
              34,
            ]}
          />

          <meshStandardMaterial
            color="#475569"
            roughness={0.52}
            metalness={0.48}
          />
        </mesh>
      ))}


      {/* CROSS SERVICE TRUNKS */}
      {[-4, 22].map((z) => (
        <mesh
          key={`cross-service-${z}`}
          position={[
            0,
            ceilingY - 1.0,
            z,
          ]}
        >
          <boxGeometry
            args={[
              70,
              0.7,
              1.45,
            ]}
          />

          <meshStandardMaterial
            color="#334155"
            roughness={0.58}
            metalness={0.4}
          />
        </mesh>
      ))}

    </group>
  );
}

function LiftShaft({
  x,
  z,
  yOffset,
  label = 'LIFT',
}) {
  const height = 9.2;

  return (
    <group>

      {/* SHAFT */}
      <mesh
        position={[
          x,
          yOffset + height / 2,
          z,
        ]}
        castShadow
        receiveShadow
      >
        <boxGeometry
          args={[
            5.2,
            height,
            5.4,
          ]}
        />

        <meshStandardMaterial
          color="#172554"
          roughness={0.55}
          metalness={0.22}
        />
      </mesh>


      {/* LIFT DOOR */}
      <mesh
        position={[
          x,
          yOffset + 2.35,
          z + 2.72,
        ]}
      >
        <boxGeometry
          args={[
            2.55,
            4.25,
            0.12,
          ]}
        />

        <meshStandardMaterial
          color="#94a3b8"
          roughness={0.28}
          metalness={0.82}
        />
      </mesh>


      {/* DOOR SPLIT */}
      <mesh
        position={[
          x,
          yOffset + 2.35,
          z + 2.79,
        ]}
      >
        <boxGeometry
          args={[
            0.035,
            4.1,
            0.035,
          ]}
        />

        <meshBasicMaterial
          color="#020617"
        />
      </mesh>


      <HorizontalLabel
        position={[
          x,
          yOffset + 0.18,
          z + 3.3,
        ]}
        size={0.42}
        color="#bae6fd"
      >
        {label}
      </HorizontalLabel>

    </group>
  );
}

function StairCore({ x, z, yOffset, label = 'STAIR' }) {
  const steps = 10;

  return (
    <group>
      {/* Stair enclosure */}
      <mesh position={[x, yOffset + 4, z]}>
        <boxGeometry args={[7, 8, 6.5]} />
        <meshStandardMaterial
          color="#1e293b"
          transparent
          opacity={0.38}
        />
      </mesh>

      {/* First flight */}
      {Array.from({ length: steps }).map((_, i) => (
        <mesh
          key={`a-${i}`}
          position={[
            x - 1.5,
            yOffset + 0.3 + i * 0.3,
            z - 2 + i * 0.4,
          ]}
        >
          <boxGeometry args={[2.5, 0.25, 0.55]} />
          <meshStandardMaterial color="#94a3b8" />
        </mesh>
      ))}

      {/* Landing */}
      <mesh position={[x, yOffset + 3.3, z + 2]}>
        <boxGeometry args={[5.5, 0.3, 1.6]} />
        <meshStandardMaterial color="#94a3b8" />
      </mesh>

      {/* Second flight */}
      {Array.from({ length: steps }).map((_, i) => (
        <mesh
          key={`b-${i}`}
          position={[
            x + 1.5,
            yOffset + 3.6 + i * 0.3,
            z + 2 - i * 0.4,
          ]}
        >
          <boxGeometry args={[2.5, 0.25, 0.55]} />
          <meshStandardMaterial color="#94a3b8" />
        </mesh>
      ))}

      <HorizontalLabel
        position={[x, yOffset + 0.15, z + 3.8]}
        size={0.4}
        color="#cbd5e1"
      >
        {label}
      </HorizontalLabel>
    </group>
  );
}

function SecureTrapCore({ x, z, yOffset }) {
  return (
    <group>
      {/* Secure room */}
      <mesh position={[x, yOffset + 2.7, z]}>
        <boxGeometry args={[10, 5.4, 7]} />
        <meshStandardMaterial
          color="#334155"
          transparent
          opacity={0.38}
        />
      </mesh>

      {/* Middle divider */}
      <mesh position={[x, yOffset + 2.7, z]}>
        <boxGeometry args={[0.18, 5.2, 6.7]} />
        <meshStandardMaterial color="#64748b" />
      </mesh>

      {/* Two secure doors */}
      {[-2.5, 2.5].map((dx) => (
        <mesh
          key={dx}
          position={[
            x + dx,
            yOffset + 2,
            z + 3.55,
          ]}
        >
          <boxGeometry args={[2, 3.6, 0.14]} />
          <meshStandardMaterial
            color="#38bdf8"
            transparent
            opacity={0.5}
            metalness={0.4}
          />
        </mesh>
      ))}

      <HorizontalLabel
        position={[x, yOffset + 0.15, z + 4.2]}
        size={0.38}
        color="#7dd3fc"
      >
        SECURE TRAP
      </HorizontalLabel>
    </group>
  );
}

function LevelOneArchitecture({
  yOffset,
  selectedRack,
  onRackSelect,
  onRackHover,
}) {
  return (
    <group>
      <BuildingShell
        yOffset={
          yOffset
        }

        label="LEVEL 1 WEST"
      />


      <FloorZone
        center={[
          0,
          9,
        ]}

        size={[
          78,
          42,
        ]}

        yOffset={
          yOffset
        }

        color="#0e7490"

        label="DATA HALL 102 - SHARON S6"
      />


      <FloorZone
        center={[
          0,
          -18,
        ]}

        size={[
          78,
          8,
        ]}

        yOffset={
          yOffset
        }

        color="#334155"

        label="MECHANICAL CORRIDOR 1C"

        opacity={
          0.34
        }
      />


      <FloorZone
        center={[
          0,
          32,
        ]}

        size={[
          78,
          6,
        ]}

        yOffset={
          yOffset
        }

        color="#334155"

        label="MECHANICAL CORRIDOR 1D"

        opacity={
          0.34
        }
      />


      <FloorZone
        center={[
          0,
          -28,
        ]}

        size={[
          78,
          10,
        ]}

        yOffset={
          yOffset
        }

        color="#475569"

        label="LIFT LOBBY / CORRIDOR 1B + RISERS"

        opacity={
          0.32
        }
      />


      <RoomOutline
        x={0}

        z={9}

        width={
          78
        }

        depth={
          42
        }

        yOffset={
          yOffset
        }
      />


      <RoomOutline
        x={0}

        z={
          -28
        }

        width={
          78
        }

        depth={
          10
        }

        yOffset={
          yOffset
        }

        color="#94a3b8"

        opacity={
          0.55
        }
      />


      <Wall
        x={0}

        z={
          -12.1
        }

        width={
          78
        }

        depth={
          0.4
        }

        yOffset={
          yOffset
        }

        height={
          3.2
        }
      />


      <Wall
        x={0}

        z={
          30.1
        }

        width={
          78
        }

        depth={
          0.4
        }

        yOffset={
          yOffset
        }

        height={
          3.2
        }
      />


      {POD_CENTERS.map(
        (
          centerX,
          index
        ) => (
          <HacsPod
            key={
              centerX
            }

            centerX={
              centerX
            }

            centerZ={
              HALL_CENTER_Z
            }

            yOffset={
              yOffset
            }

            number={
              index +
              1
            }
          />
        )
      )}


      <FloorRacks
        racks={
          LEVEL1_RACKS
        }

        yOffset={
          yOffset
        }

        selectedRack={
          selectedRack
        }

        onRackSelect={
          onRackSelect
        }

        onRackHover={
          onRackHover
        }
      />


      <PrimaryContainment
        yOffset={
          yOffset
        }
      />

      <OverheadServices
  yOffset={yOffset}
/>


      <RowCores
        yOffset={
          yOffset
        }
      />


      <Penetration
        x={
          -37
        }

        z={23}

        yOffset={
          yOffset
        }
      />


      <Penetration
        x={37}

        z={23}

        yOffset={
          yOffset
        }
      />


      {ROW_XS.map(
        (
          x,
          index
        ) => (
          <HorizontalLabel
            key={x}

            position={[
              x,

              yOffset +
                0.16,

              -5.8,
            ]}

            size={
              0.34
            }

            color="#94a3b8"
          >
            ROW {index + 1}
          </HorizontalLabel>
        )
      )}

            {/* LEVEL 1 ACCESS / LIFT CORE */}

      <StairCore
        x={-32}
        z={-28}
        yOffset={yOffset}
        label="STAIR 1"
      />

      <LiftShaft
        x={-12}
        z={-28}
        yOffset={yOffset}
        label="LIFT 1"
      />

      <LiftShaft
        x={12}
        z={-28}
        yOffset={yOffset}
        label="LIFT 2"
      />

      <StairCore
        x={32}
        z={-28}
        yOffset={yOffset}
        label="STAIR 2"
      />

    </group>
  );
}

/* =========================================================
   LEVEL 2 ARCHITECTURE
========================================================= */

function LevelTwoArchitecture({
  yOffset,
  selectedRack,
  onRackSelect,
  onRackHover,
}) {
  return (
    <group>
      <BuildingShell
        yOffset={
          yOffset
        }

        label="LEVEL 2 WEST"
      />


      <FloorZone
        center={[
          0,
          9,
        ]}

        size={[
          78,
          42,
        ]}

        yOffset={
          yOffset
        }

        color="#0e7490"

        label="DATA HALL 202 - SHARON S6"
      />


      <FloorZone
        center={[
          0,
          -18,
        ]}

        size={[
          78,
          8,
        ]}

        yOffset={
          yOffset
        }

        color="#334155"

        label="CORRIDOR 2C / ACCESS"

        opacity={
          0.34
        }
      />


      <FloorZone
        center={[
          0,
          32,
        ]}

        size={[
          78,
          6,
        ]}

        yOffset={
          yOffset
        }

        color="#334155"

        label="MECHANICAL CORRIDOR 2D"

        opacity={
          0.34
        }
      />


      <FloorZone
        center={[
          33,
          -28,
        ]}

        size={[
          12,
          10,
        ]}

        yOffset={
          yOffset
        }

        color="#475569"

        label="SECURE TRAP 2A"

        opacity={
          0.34
        }
      />


      <FloorZone
        center={[
          -23,
          -28,
        ]}

        size={[
          42,
          10,
        ]}

        yOffset={
          yOffset
        }

        color="#475569"

        label="LIFT / RISERS / ACCESS"

        opacity={
          0.3
        }
      />


      <RoomOutline
        x={0}

        z={9}

        width={
          78
        }

        depth={
          42
        }

        yOffset={
          yOffset
        }
      />


      <RoomOutline
        x={33}

        z={
          -28
        }

        width={
          12
        }

        depth={
          10
        }

        yOffset={
          yOffset
        }

        color="#94a3b8"

        opacity={
          0.56
        }
      />


      <Wall
        x={0}

        z={
          -12.1
        }

        width={
          78
        }

        depth={
          0.4
        }

        yOffset={
          yOffset
        }

        height={
          3.2
        }
      />


      <Wall
        x={0}

        z={
          30.1
        }

        width={
          78
        }

        depth={
          0.4
        }

        yOffset={
          yOffset
        }

        height={
          3.2
        }
      />


      {POD_CENTERS.map(
        (
          centerX,
          index
        ) => (
          <HacsPod
            key={
              centerX
            }

            centerX={
              centerX
            }

            centerZ={
              HALL_CENTER_Z
            }

            yOffset={
              yOffset
            }

            number={
              index +
              1
            }
          />
        )
      )}


      <FloorRacks
        racks={
          LEVEL2_RACKS
        }

        yOffset={
          yOffset
        }

        selectedRack={
          selectedRack
        }

        onRackSelect={
          onRackSelect
        }

        onRackHover={
          onRackHover
        }
      />


      <PrimaryContainment
        yOffset={
          yOffset
        }
      />

      <OverheadServices
  yOffset={yOffset}
/>


      <RowCores
        yOffset={
          yOffset
        }
      />


      <Penetration
        x={
          -37
        }

        z={23}

        yOffset={
          yOffset
        }
      />


      <Penetration
        x={37}

        z={23}

        yOffset={
          yOffset
        }
      />


      {ROW_XS.map(
        (
          x,
          index
        ) => (
          <HorizontalLabel
            key={x}

            position={[
              x,

              yOffset +
                0.16,

              -5.8,
            ]}

            size={
              0.34
            }

            color="#94a3b8"
          >
            ROW {index + 1}
          </HorizontalLabel>
        )
      )}


<FloorZone
  center={[
    0,
    -35.2,
  ]}

  size={[
    78,
    4.5,
  ]}

  yOffset={
    yOffset
  }

  color="#111827"

  label="DATA HALL 201 - ARCHITECTURAL CONTEXT / OUT OF SHARON TWIN SCOPE"

  labelColor="#64748b"

  opacity={
    0.2
  }
/>


{/* LEVEL 2 ACCESS / SECURE AREA */}

<StairCore
  x={-33}
  z={-28}
  yOffset={yOffset}
  label="STAIR"
/>

<LiftShaft
  x={-17}
  z={-28}
  yOffset={yOffset}
  label="LIFT 1"
/>

<LiftShaft
  x={-8}
  z={-28}
  yOffset={yOffset}
  label="LIFT 2"
/>

<SecureTrapCore
  x={33}
  z={-28}
  yOffset={yOffset}
/>


</group>
);
}


/* =========================================================
   VERTICAL BASKET
========================================================= */

function VerticalBasketRiser() {
  const floorHeight =
    SHARON_S6_MODEL
      .building
      .floorToFloor;

  const x = 38;
  const z = 23;

  return (
    <group>
      <mesh
        position={[
          x,
          floorHeight / 2 + 0.2,
          z,
        ]}
      >
        <boxGeometry
          args={[
            1.15,
            floorHeight - 1,
            0.72,
          ]}
        />

        <meshStandardMaterial
          color="#d8fbff"
          wireframe
          transparent
          opacity={0.78}
          emissive="#67e8f9"
          emissiveIntensity={0.28}
        />
      </mesh>


      <Text
        position={[
          x - 2.2,
          floorHeight / 2,
          z,
        ]}
        fontSize={0.58}
        color="#a5f3fc"
        anchorX="center"
        anchorY="middle"
      >
        {'600 mm vertical basket\nLevel 2 ↔ Level 1'}
      </Text>

    </group>
  );
}


/* =========================================================
   CAMERA LIMITS
========================================================= */

function clampNavigation(
  camera,
  controls
) {
  camera.position.x =
    THREE.MathUtils.clamp(
      camera.position.x,

      CAMERA_LIMITS.minX,

      CAMERA_LIMITS.maxX
    );


  camera.position.y =
    THREE.MathUtils.clamp(
      camera.position.y,

      CAMERA_LIMITS.minY,

      CAMERA_LIMITS.maxY
    );


  camera.position.z =
    THREE.MathUtils.clamp(
      camera.position.z,

      CAMERA_LIMITS.minZ,

      CAMERA_LIMITS.maxZ
    );


  if (
    !controls
  ) {
    return;
  }


  controls.target.x =
    THREE.MathUtils.clamp(
      controls.target.x,

      CAMERA_LIMITS.minX,

      CAMERA_LIMITS.maxX
    );


  controls.target.y =
    THREE.MathUtils.clamp(
      controls.target.y,

      CAMERA_LIMITS.minTargetY,

      CAMERA_LIMITS.maxTargetY
    );


  controls.target.z =
    THREE.MathUtils.clamp(
      controls.target.z,

      CAMERA_LIMITS.minZ,

      CAMERA_LIMITS.maxZ
    );
}


/* =========================================================
   VIEW CAMERA ANIMATION
========================================================= */

function CameraAnimator({
  mode,
  topView,
  controlsRef,
  disabled,
  cancelAnimationRef,
}) {
  const {
    camera,
  } =
    useThree();


  const targetPosition =
    useRef(
      new THREE.Vector3()
    );


  const targetLookAt =
    useRef(
      new THREE.Vector3()
    );


  const animating =
    useRef(
      true
    );


  useEffect(
    () => {
      if (
        cancelAnimationRef
      ) {
        cancelAnimationRef.current =
          false;
      }


      if (
        topView
      ) {
        camera.up.set(
          0,
          0,
          -1
        );


        targetPosition.current.set(
          0.01,

          mode ===
            'stacked'
            ? 116
            : 94,

          HALL_CENTER_Z
        );


        targetLookAt.current.set(
          0,

          mode ===
            'stacked'
            ? SHARON_S6_MODEL
                .building
                .floorToFloor /
                2
            : 0,

          HALL_CENTER_Z
        );
      } else {
        camera.up.set(
          0,
          1,
          0
        );


        if (
          mode ===
          'stacked'
        ) {
          const centerY =
            SHARON_S6_MODEL
              .building
              .floorToFloor /
              2 +
            3;


          targetPosition.current.set(
            0,
            centerY,
            108
          );


          targetLookAt.current.set(
            0,
            centerY,
            HALL_CENTER_Z
          );
        } else {
          targetPosition.current.set(
            0,
            5.5,
            98
          );


          targetLookAt.current.set(
            0,
            5.5,
            HALL_CENTER_Z
          );
        }
      }


      animating.current =
        true;
    },

    [
      mode,
      topView,
      camera,
      cancelAnimationRef,
    ]
  );


  useFrame(
    (
      _,
      delta
    ) => {
      if (
        disabled
      ) {
        animating.current =
          false;

        return;
      }


      if (
        !animating.current
      ) {
        return;
      }


      if (
        cancelAnimationRef
          ?.current
      ) {
        animating.current =
          false;

        return;
      }


      const controls =
        controlsRef.current;


      const alpha =
        1 -
        Math.exp(
          -4.2 *
            delta
        );


      camera.position.lerp(
        targetPosition.current,

        alpha
      );


      if (
        controls
      ) {
        controls.target.lerp(
          targetLookAt.current,

          alpha
        );


        controls.update();
      }


      const cameraDone =
        camera.position.distanceTo(
          targetPosition.current
        ) <
        0.08;


      const targetDone =
        !controls ||
        controls.target.distanceTo(
          targetLookAt.current
        ) <
          0.08;


      if (
        cameraDone &&
        targetDone
      ) {
        animating.current =
          false;
      }
    }
  );


  return null;
}


/* =========================================================
   CLICKED RACK FOCUS
========================================================= */

/* =========================================================
   RACK FOCUS CAMERA

   Same control flow as the OG twin:
   Auto Roam stops first, then this animator smoothly moves
   from the CURRENT camera position to the selected rack.
========================================================= */

function RackFocusAnimator({
  selectedRack,
  levelMode,
  controlsRef,
  isTouring,
}) {
  const { camera } =
    useThree();


  const animTarget =
    useRef(null);


  useEffect(() => {
    /*
     * Auto Roam owns the camera
     * while it is running.
     */
    if (
      isTouring
    ) {
      animTarget.current =
        null;

      return;
    }


    if (
      !selectedRack
    ) {
      animTarget.current =
        null;

      return;
    }


    /*
     * In ALL mode L2 exists at
     * floorToFloor elevation.
     *
     * In individual L1/L2 mode
     * the visible level is rendered
     * at y = 0.
     */
    const levelOffset =
      levelMode === 'stacked' &&
      selectedRack.level === 'level2'
        ? SHARON_S6_MODEL
            .building
            .floorToFloor
        : 0;


    /*
     * Rows are generated as pairs:
     *
     * 1,3,5,7 = left side of HAC
     * 2,4,6,8 = right side of HAC
     *
     * Approach from the outside/cold aisle,
     * not through the HAC or another rack.
     */
    const isLeftRow =
      selectedRack.row % 2 === 1;


    const frontOffsetX =
      isLeftRow
        ? -6.2
        : 6.2;


    animTarget.current = {
      camPos:
        new THREE.Vector3(
          selectedRack.x +
            frontOffsetX,

          levelOffset +
            4.8,

          selectedRack.z +
            0.2
        ),

      lookAt:
        new THREE.Vector3(
          selectedRack.x,

          levelOffset +
            3.1,

          selectedRack.z
        ),
    };


    /*
     * Ensure a normal upright camera
     * after Auto Roam / Top View.
     */
    camera.up.set(
      0,
      1,
      0
    );
  }, [
    selectedRack,
    levelMode,
    isTouring,
    camera,
  ]);


  useFrame(
    (
      _,
      delta
    ) => {
      const controls =
        controlsRef.current;


      if (
        !controls ||
        !animTarget.current ||
        isTouring
      ) {
        return;
      }


      /*
       * Prevent a dropped rendering frame
       * from causing a giant camera jump.
       */
      const dt =
        Math.min(
          delta,
          1 / 30
        );


      const alpha =
        1 -
        Math.exp(
          -4.8 *
            dt
        );


      camera.position.lerp(
        animTarget.current
          .camPos,

        alpha
      );


      controls.target.lerp(
        animTarget.current
          .lookAt,

        alpha
      );


      controls.update();


      const cameraDone =
        camera.position
          .distanceTo(
            animTarget.current
              .camPos
          ) <
        0.12;


      const targetDone =
        controls.target
          .distanceTo(
            animTarget.current
              .lookAt
          ) <
        0.12;


      if (
        cameraDone &&
        targetDone
      ) {
        /*
         * Snap the last tiny fraction
         * so there is no endless micro-motion.
         */
        camera.position.copy(
          animTarget.current
            .camPos
        );


        controls.target.copy(
          animTarget.current
            .lookAt
        );


        controls.update();


        /*
         * STOP HERE.
         *
         * The camera no longer keeps
         * trying to animate.
         */
        animTarget.current =
          null;
      }
    }
  );


  return null;
}


/* =========================================================
   SHARON S6 AUTO ROAM
   R = START / STOP

   Single level:
   - roams through all 4 HAC corridors
   - visually covers all 8 rack rows

   ALL mode:
   - roams Level 1
   - rises outside the hall
   - roams Level 2
========================================================= */

function TourAnimator({
  controlsRef,
  isTouring,
  levelMode,
}) {
  const tourPath =
    useMemo(() => {
      const floorHeight =
        SHARON_S6_MODEL
          .building
          .floorToFloor;

      const WALK_Y =
        3.8;

      const aisleXs = [
        -27,
        -9,
        9,
        27,
      ];

      const frontZ =
        22;

      const rearZ =
        -4;

      const outsideX =
        42;

      /*
 * Temporary Auto-Roam elevator.
 * Matches VerticalBasketRiser position.
 */
const elevatorX =
  38;

const elevatorZ =
  23;


      const buildLevelRoute = (
        y,
        fromLeft = true
      ) => {
        if (fromLeft) {
          return [
            new THREE.Vector3(
              -outsideX,
              y,
              frontZ
            ),

            new THREE.Vector3(
              -36,
              y,
              frontZ
            ),

            new THREE.Vector3(
              aisleXs[0],
              y,
              frontZ
            ),

            new THREE.Vector3(
              aisleXs[0],
              y,
              rearZ
            ),

            new THREE.Vector3(
              aisleXs[1],
              y,
              rearZ
            ),

            new THREE.Vector3(
              aisleXs[1],
              y,
              frontZ
            ),

            new THREE.Vector3(
              aisleXs[2],
              y,
              frontZ
            ),

            new THREE.Vector3(
              aisleXs[2],
              y,
              rearZ
            ),

            new THREE.Vector3(
              aisleXs[3],
              y,
              rearZ
            ),

            new THREE.Vector3(
              aisleXs[3],
              y,
              frontZ
            ),

            new THREE.Vector3(
              36,
              y,
              frontZ
            ),

            new THREE.Vector3(
              outsideX,
              y,
              frontZ
            ),
          ];
        }


        return [
          new THREE.Vector3(
            outsideX,
            y,
            frontZ
          ),

          new THREE.Vector3(
            36,
            y,
            frontZ
          ),

          new THREE.Vector3(
            aisleXs[3],
            y,
            frontZ
          ),

          new THREE.Vector3(
            aisleXs[3],
            y,
            rearZ
          ),

          new THREE.Vector3(
            aisleXs[2],
            y,
            rearZ
          ),

          new THREE.Vector3(
            aisleXs[2],
            y,
            frontZ
          ),

          new THREE.Vector3(
            aisleXs[1],
            y,
            frontZ
          ),

          new THREE.Vector3(
            aisleXs[1],
            y,
            rearZ
          ),

          new THREE.Vector3(
            aisleXs[0],
            y,
            rearZ
          ),

          new THREE.Vector3(
            aisleXs[0],
            y,
            frontZ
          ),

          new THREE.Vector3(
            -36,
            y,
            frontZ
          ),

          new THREE.Vector3(
            -outsideX,
            y,
            frontZ
          ),
        ];
      };


      let rawPts;


      if (
  levelMode ===
  'stacked'
) {
  const level1Route =
    buildLevelRoute(
      WALK_Y,
      true
    );


  const level2Y =
    floorHeight +
    WALK_Y;


  const level2Route =
    buildLevelRoute(
      level2Y,
      false
    );


  rawPts = [
    /*
     * ==========================================
     * LEVEL 1 AUTO ROAM
     * ==========================================
     */
    ...level1Route,


    /*
     * ==========================================
     * APPROACH THE VERTICAL BASKET
     * ==========================================
     *
     * Move from the L1 exit toward the
     * vertical basket / temporary elevator.
     */

    new THREE.Vector3(
      40,
      WALK_Y,
      22
    ),

    new THREE.Vector3(
      elevatorX,
      WALK_Y,
      elevatorZ
    ),


    /*
     * ==========================================
     * ELEVATOR ASCENT
     * ==========================================
     *
     * Same X and Z.
     * ONLY Y changes.
     *
     * This makes the camera rise vertically
     * inside the basket rather than flying
     * diagonally between floors.
     */

    new THREE.Vector3(
      elevatorX,
      WALK_Y + 2,
      elevatorZ
    ),

    new THREE.Vector3(
      elevatorX,
      level2Y - 2,
      elevatorZ
    ),

    new THREE.Vector3(
      elevatorX,
      level2Y,
      elevatorZ
    ),


    /*
     * ==========================================
     * EXIT ELEVATOR ON LEVEL 2
     * ==========================================
     */

    new THREE.Vector3(
      40,
      level2Y,
      22
    ),


    /*
     * ==========================================
     * LEVEL 2 AUTO ROAM
     * ==========================================
     */
    ...level2Route,
  ];
} else {
  rawPts =
    buildLevelRoute(
      WALK_Y,
      true
    );
}


      const TURN_RADIUS =
        1.15;


      const parts =
        [];


      let currentStart =
        rawPts[0].clone();


      for (
        let i = 1;
        i <
        rawPts.length - 1;
        i += 1
      ) {
        const prev =
          rawPts[i - 1];

        const corner =
          rawPts[i];

        const next =
          rawPts[i + 1];


        const inVec =
          corner
            .clone()
            .sub(prev);


        const outVec =
          next
            .clone()
            .sub(corner);


        const inLen =
          inVec.length();


        const outLen =
          outVec.length();

        /*
 * Keep elevator travel perfectly vertical.
 *
 * Do NOT create rounded Bezier turns
 * when either incoming or outgoing
 * movement is primarily vertical.
 */
const inVertical =
  Math.abs(inVec.y) >
  Math.abs(inVec.x) +
  Math.abs(inVec.z);

const outVertical =
  Math.abs(outVec.y) >
  Math.abs(outVec.x) +
  Math.abs(outVec.z);


if (
  inVertical ||
  outVertical
) {
  if (
    currentStart.distanceTo(
      corner
    ) >
    0.001
  ) {
    const verticalLine =
      new THREE.LineCurve3(
        currentStart.clone(),
        corner.clone()
      );


    parts.push({
      curve:
        verticalLine,

      length:
        verticalLine.getLength(),
    });
  }


  currentStart =
    corner.clone();

  continue;
}

        if (
          inLen < 0.001 ||
          outLen < 0.001
        ) {
          continue;
        }


        const inDir =
          inVec
            .clone()
            .normalize();


        const outDir =
          outVec
            .clone()
            .normalize();


        const dot =
          inDir.dot(
            outDir
          );


        if (
          dot > 0.985
        ) {
          continue;
        }


        const trim =
          Math.min(
            TURN_RADIUS,

            inLen *
              0.32,

            outLen *
              0.32
          );


        const curveStart =
          corner
            .clone()
            .sub(
              inDir
                .clone()
                .multiplyScalar(
                  trim
                )
            );


        const curveEnd =
          corner
            .clone()
            .add(
              outDir
                .clone()
                .multiplyScalar(
                  trim
                )
            );


        if (
          currentStart
            .distanceTo(
              curveStart
            ) >
          0.001
        ) {
          const line =
            new THREE.LineCurve3(
              currentStart.clone(),
              curveStart.clone()
            );


          parts.push({
            curve:
              line,

            length:
              line.getLength(),
          });
        }


        const curve =
          new THREE
            .QuadraticBezierCurve3(
              curveStart.clone(),
              corner.clone(),
              curveEnd.clone()
            );


        parts.push({
          curve,

          length:
            curve.getLength(),
        });


        currentStart =
          curveEnd.clone();
      }


      const last =
        rawPts[
          rawPts.length -
            1
        ].clone();


      if (
        currentStart
          .distanceTo(
            last
          ) >
        0.001
      ) {
        const finalLine =
          new THREE.LineCurve3(
            currentStart.clone(),
            last.clone()
          );


        parts.push({
          curve:
            finalLine,

          length:
            finalLine.getLength(),
        });
      }


      const totalLength =
        parts.reduce(
          (
            sum,
            part
          ) =>
            sum +
            part.length,

          0
        );


      const sample =
        (
          distance
        ) => {
          let remaining =
            THREE.MathUtils.clamp(
              distance,
              0,
              totalLength
            );


          for (
            const part of
            parts
          ) {
            if (
              remaining <=
              part.length
            ) {
              const t =
                part.length ===
                0
                  ? 0
                  : remaining /
                    part.length;


              return part.curve
                .getPoint(
                  t
                );
            }


            remaining -=
              part.length;
          }


          return last.clone();
        };


      return {
        sample,
        totalLength,
      };
    },

    [
      levelMode,
    ]
  );


  const distanceTravelled =
    useRef(0);


  const startDelay =
    useRef(0);


  const transitionProgress =
    useRef(0);


  const startCamPos =
    useRef(
      new THREE.Vector3()
    );

  const startTarget =
  useRef(
    new THREE.Vector3()
  );

  const wasTouring =
    useRef(false);

  /*
 * Keeps camera heading stable during
 * vertical elevator travel.
 *
 * Auto Roam can move vertically,
 * but the camera must never look
 * directly up or down.
 */
const lastHorizontalLookDir =
  useRef(
    new THREE.Vector3(
      -1,
      0,
      0
    )
  );


  useFrame(
  (
    state,
    delta
  ) => {
    /*
     * IMPORTANT PERFORMANCE FIX
     *
     * If one browser frame takes unusually
     * long, don't allow that giant delta
     * to produce a camera jump.
     */
    const dt =
      Math.min(
        delta,
        1 / 30
      );


    /*
     * Auto Roam is off.
     *
     * Reset all tour progress so the next
     * press of R starts cleanly.
     */
    if (
      !isTouring ||
      !controlsRef.current
    ) {
      if (
        !isTouring
      ) {
        distanceTravelled.current =
          0;

        startDelay.current =
          0;

        transitionProgress.current =
          0;

        wasTouring.current =
          false;
      }

      return;
    }


    /*
     * AUTO ROAM JUST STARTED
     *
     * Capture the camera's CURRENT position.
     *
     * This means the initial transition begins
     * exactly from wherever the user currently is.
     */
    /* =====================================================
   AUTO ROAM JUST STARTED
===================================================== */

/* =====================================================
   AUTO ROAM JUST STARTED
===================================================== */

if (
  !wasTouring.current
) {
  distanceTravelled.current =
    0;

  startDelay.current =
    0.55;

  transitionProgress.current =
    0;


  /*
   * Capture the exact camera position
   * and target where Auto Roam was started.
   */
  startCamPos.current.copy(
    state.camera.position
  );


  startTarget.current.copy(
    controlsRef.current.target
  );


  /*
   * Preserve the current horizontal heading
   * for the later elevator anti-flip logic.
   */
  const startForward =
    new THREE.Vector3();


  state.camera.getWorldDirection(
    startForward
  );


  startForward.y =
    0;


  if (
    startForward.lengthSq() >
    0.0001
  ) {
    startForward.normalize();


    lastHorizontalLookDir.current.copy(
      startForward
    );
  }


  wasTouring.current =
    true;
}


/* =====================================================
   AUTO ROAM ENTRY
===================================================== */

const entryCamPos =
  tourPath.sample(
    0
  );


const entryLookTarget =
  tourPath.sample(
    Math.min(
      3.5,
      tourPath.totalLength
    )
  );

/*
 * Exact direction of the Auto Roam route
 * when entering the first aisle.
 *
 * We use this to hand camera control from
 * the intro transition to the normal tour
 * without a one-frame direction flip.
 */
const entryDirection =
  entryLookTarget
    .clone()
    .sub(entryCamPos);

entryDirection.y = 0;

if (entryDirection.lengthSq() > 0.0001) {
  entryDirection.normalize();
}


/* =====================================================
   SMOOTH ORIGINAL START TRANSITION
===================================================== */

if (
  transitionProgress.current <
  1
) {
  /*
   * One continuous animation.
   *
   * No separate rise,
   * no horizontal stage,
   * no separate descent.
   */
  transitionProgress.current =
    Math.min(
      1,

      transitionProgress.current +
        dt * 0.75
    );


  /*
   * Smooth cubic ease-out.
   *
   * Fast enough to feel responsive,
   * but slows naturally as it reaches
   * the Auto Roam entrance.
   */
  const eased =
    1 -
    Math.pow(
      1 -
        transitionProgress.current,

      3
    );


  /*
   * Smooth camera movement directly
   * from the CURRENT camera position
   * to the tour entrance.
   */
  state.camera.position
    .copy(
      startCamPos.current
    )
    .lerp(
      entryCamPos,
      eased
    );


  /*
   * Smoothly blend the camera's look
   * target into the direction of travel.
   */
  controlsRef.current
    .target
    .copy(
      startTarget.current
    )
    .lerp(
      entryLookTarget,
      eased
    );


  /*
   * Keep camera upright.
   */
  state.camera.up.set(
    0,
    1,
    0
  );


  state.camera.lookAt(
  controlsRef.current.target
);

/*
 * IMPORTANT HANDOFF FIX
 *
 * Once the entry animation reaches the
 * Auto Roam path, force the remembered
 * camera heading to match the path itself.
 *
 * Otherwise the first normal tour frame
 * briefly reuses the old pre-tour heading,
 * which causes the split-second second flip.
 */

if (
  transitionProgress.current >= 1 &&
  entryDirection.lengthSq() > 0.0001
) {
  lastHorizontalLookDir.current.copy(
    entryDirection
  );

  /*
   * Snap the final tiny fraction so
   * transition and tour begin from the
   * exact same camera state.
   */
  state.camera.position.copy(
    entryCamPos
  );

  controlsRef.current.target.copy(
    entryLookTarget
  );

  state.camera.lookAt(
    entryLookTarget
  );
}


  /*
   * Very important:
   * normal walkthrough movement does
   * not run until this transition ends.
   */
  return;
}


    /*
     * =====================================================
     * SHORT CINEMATIC PAUSE AT ENTRY
     * =====================================================
     */
    /*
 * =====================================================
 * SHORT PAUSE AT THE TOUR / ELEVATOR ENTRY
 * =====================================================
 */
if (
  startDelay.current >
  0
) {
  startDelay.current =
    Math.max(
      0,

      startDelay.current -
        dt
    );
} else {
  /*
   * ===================================================
   * AUTO ROAM MOVEMENT
   * ===================================================
   *
   * Keep the existing smooth tour speed.
   */
  distanceTravelled.current =
    Math.min(
      tourPath.totalLength,

      distanceTravelled.current +
        dt * 4.2
    );
}


/*
 * =====================================================
 * CURRENT CAMERA POSITION
 * =====================================================
 */
const camPos =
  tourPath.sample(
    distanceTravelled.current
  );


/*
 * Sample slightly ahead on the route.
 *
 * Normally this gives us the direction
 * that the camera should face.
 */
const rawLookTarget =
  tourPath.sample(
    Math.min(
      distanceTravelled.current +
        3.5,

      tourPath.totalLength
    )
  );


/*
 * =====================================================
 * STABLE HORIZONTAL CAMERA DIRECTION
 * =====================================================
 *
 * IMPORTANT:
 *
 * During the basket/elevator section,
 * the camera moves vertically.
 *
 * If camera.lookAt() points directly
 * upward or downward, Three.js can
 * produce an unstable orientation,
 * causing the camera to spin or flip.
 *
 * Therefore the camera is ALWAYS
 * kept facing horizontally.
 */
const horizontalDirection =
  new THREE.Vector3(
    rawLookTarget.x -
      camPos.x,

    0,

    rawLookTarget.z -
      camPos.z
  );


/*
 * If there is meaningful horizontal
 * movement, gradually update the
 * remembered viewing direction.
 *
 * The lerp gives us a smoother turn
 * instead of snapping direction.
 */
if (
  horizontalDirection.lengthSq() >
  0.0001
) {
  horizontalDirection.normalize();


const turnAlpha =
  1 -
  Math.exp(
    -6 * dt
  );

lastHorizontalLookDir.current
  .lerp(
    horizontalDirection,
    turnAlpha
  )
  .normalize();
}

/*
 * =====================================================
 * STABLE LOOK TARGET
 * =====================================================
 *
 * Even while the camera rises vertically,
 * this target stays horizontally in front
 * of the camera.
 *
 * Therefore:
 *
 * camera rises ↑
 * camera keeps looking →
 *
 * instead of looking ↑ and flipping.
 */
const stableLookTarget =
  new THREE.Vector3(
    camPos.x +
      lastHorizontalLookDir.current.x *
        5,

    camPos.y,

    camPos.z +
      lastHorizontalLookDir.current.z *
        5
  );


/*
 * =====================================================
 * APPLY CAMERA POSITION
 * =====================================================
 */
state.camera.position.copy(
  camPos
);


/*
 * Keep OrbitControls synchronized
 * with the same stable look target.
 */
controlsRef.current
  .target
  .copy(
    stableLookTarget
  );


/*
 * Force a normal upright camera.
 */
state.camera.up.set(
  0,
  1,
  0
);


/*
 * =====================================================
 * FINAL CAMERA ORIENTATION
 * =====================================================
 *
 * This is now ALWAYS horizontal,
 * including during the elevator ride.
 */
state.camera.lookAt(
  stableLookTarget
);
  }
);


  return null;
}

/* =========================================================
   KEYBOARD NAVIGATION
========================================================= */

function KeyboardNavigation({
  controlsRef,
  disabled,
  cancelAnimationRef,
}) {
  const {
    camera,
  } =
    useThree();


  const keys =
    useRef(
      new Set()
    );


  useEffect(
    () => {
      const onDown =
        (
          event
        ) => {
          const tag =
            event.target
              ?.tagName;


          if (
            [
              'INPUT',
              'TEXTAREA',
              'SELECT',
            ].includes(
              tag
            )
          ) {
            return;
          }


          keys.current.add(
            event.key.toLowerCase()
          );
        };


      const onUp =
        (
          event
        ) => {
          keys.current.delete(
            event.key.toLowerCase()
          );
        };


      const onBlur =
        () => {
          keys.current.clear();
        };


      window.addEventListener(
        'keydown',
        onDown
      );


      window.addEventListener(
        'keyup',
        onUp
      );


      window.addEventListener(
        'blur',
        onBlur
      );


      return () => {
        window.removeEventListener(
          'keydown',
          onDown
        );


        window.removeEventListener(
          'keyup',
          onUp
        );


        window.removeEventListener(
          'blur',
          onBlur
        );
      };
    },

    []
  );


  useFrame(
    (
      _,
      delta
    ) => {
      if (
        disabled ||
        keys.current.size ===
          0
      ) {
        return;
      }


      if (
        cancelAnimationRef
      ) {
        cancelAnimationRef.current =
          true;
      }


      const controls =
        controlsRef.current;


      const speed =
        (keys.current.has(
          'shift'
        )
          ? 38
          : 18) *
        delta;


      const forward =
        new THREE.Vector3();


      camera.getWorldDirection(
        forward
      );


      forward.y =
        0;


      forward.normalize();


      const right =
        new THREE.Vector3()
          .crossVectors(
            forward,
            camera.up
          )
          .normalize();


      const move =
        new THREE.Vector3();


      if (
        keys.current.has(
          'w'
        ) ||
        keys.current.has(
          'arrowup'
        )
      ) {
        move.add(
          forward
        );
      }


      if (
        keys.current.has(
          's'
        ) ||
        keys.current.has(
          'arrowdown'
        )
      ) {
        move.sub(
          forward
        );
      }


      if (
        keys.current.has(
          'd'
        ) ||
        keys.current.has(
          'arrowright'
        )
      ) {
        move.add(
          right
        );
      }


      if (
        keys.current.has(
          'a'
        ) ||
        keys.current.has(
          'arrowleft'
        )
      ) {
        move.sub(
          right
        );
      }


      if (
        keys.current.has(
          'e'
        )
      ) {
        move.y +=
          1;
      }


      if (
        keys.current.has(
          'q'
        )
      ) {
        move.y -=
          1;
      }


      if (
        !move.lengthSq()
      ) {
        return;
      }


      move
        .normalize()
        .multiplyScalar(
          speed
        );


      camera.position.add(
        move
      );


      controls?.target.add(
        move
      );


      clampNavigation(
        camera,
        controls
      );


      controls?.update();
    }
  );


  return null;
}


/* =========================================================
   CAMERA BOUNDS
========================================================= */

function CameraBounds({
  controlsRef,
}) {
  const {
    camera,
  } =
    useThree();


  useFrame(
    () => {
      clampNavigation(
        camera,
        controlsRef.current
      );
    }
  );


  return null;
}


/* =========================================================
   THREE.JS SCENE
========================================================= */

function Scene({
  levelMode,
  topView,
  controlsRef,
  selectedRack,
  onRackSelect,
  onRackHover,
  cancelAnimationRef,
  isTouring,
}) {
  const stacked =
    levelMode ===
    'stacked';


  const level2Y =
    stacked
      ? SHARON_S6_MODEL
          .building
          .floorToFloor
      : 0;


  return (
    <>
      <color
        attach="background"

        args={[
          '#092235',
        ]}
      />


      <fog
        attach="fog"

        args={[
          '#092235',
          76,
          185,
        ]}
      />


      <ambientLight
        intensity={
          0.62
        }

        color="#d7fbff"
      />


      <directionalLight
        position={[
          34,
          58,
          28,
        ]}

        intensity={
          1.5
        }

        color="#effdff"

        castShadow
      />


      <directionalLight
        position={[
          -32,
          30,
          -18,
        ]}

        intensity={
          0.44
        }

        color="#67e8f9"
      />


      {(levelMode ===
        'level1' ||
        stacked) && (
        <LevelOneArchitecture
          yOffset={0}

          selectedRack={
            selectedRack
          }

          onRackSelect={
            onRackSelect
          }

          onRackHover={
            onRackHover
          }
        />
      )}


      {(levelMode ===
        'level2' ||
        stacked) && (
        <LevelTwoArchitecture
          yOffset={
            level2Y
          }

          selectedRack={
            selectedRack
          }

          onRackSelect={
            onRackSelect
          }

          onRackHover={
            onRackHover
          }
        />
      )}


      {stacked && (
        <VerticalBasketRiser />
      )}


      <TourAnimator
  controlsRef={
    controlsRef
  }

  isTouring={
    isTouring
  }

  levelMode={
    levelMode
  }
/>
      
      <CameraAnimator
        mode={
          levelMode
        }

        topView={
          topView
        }

        controlsRef={
          controlsRef
        }

        disabled={
  Boolean(
    selectedRack
  ) ||
  isTouring
}

        cancelAnimationRef={
          cancelAnimationRef
        }
      />


      <RackFocusAnimator
  selectedRack={
    selectedRack
  }

  levelMode={
    levelMode
  }

  controlsRef={
    controlsRef
  }

  isTouring={
    isTouring
  }
/>


      <KeyboardNavigation
        controlsRef={
          controlsRef
        }

        disabled={
          topView ||
          isTouring
        }

        cancelAnimationRef={
          cancelAnimationRef
        }
      />


      <CameraBounds
        controlsRef={
          controlsRef
        }
      />


      <OrbitControls
  ref={controlsRef}
  makeDefault

  enabled={!isTouring}

  enableRotate={
    !topView &&
    !isTouring
  }

  enablePan={
    !isTouring
  }

  enableZoom={
    !isTouring
  }

  target={[
    0,
    stacked ? 12 : 5.5,
    HALL_CENTER_Z,
  ]}

  minDistance={16}
  maxDistance={135}

  minPolarAngle={0}
  maxPolarAngle={Math.PI / 2}

  minAzimuthAngle={
    THREE.MathUtils.degToRad(-130)
  }

  maxAzimuthAngle={
    THREE.MathUtils.degToRad(130)
  }

  enableDamping
  dampingFactor={0.055}

  rotateSpeed={0.5}
  panSpeed={0.7}
  zoomSpeed={0.38}

  screenSpacePanning
/>
    </>
  );
}


/* =========================================================
   INTRO
========================================================= */

function Intro({
  onDone,
}) {
  useEffect(
    () => {
      const timer =
        setTimeout(
          onDone,
          1500
        );


      return () =>
        clearTimeout(
          timer
        );
    },

    [
      onDone,
    ]
  );


  return (
    <div className="s6-intro">
      <div className="s6-intro-mark">
        S6
      </div>

      <div className="s6-intro-title">
        Sharon S6
      </div>

      <div className="s6-intro-subtitle">
        Data Center Architecture
      </div>
    </div>
  );
}


/* =========================================================
   GLASS BUTTON
========================================================= */

function GlassButton({
  active,
  title,
  onClick,
  children,
}) {
  return (
    <button
      type="button"

      title={
        title
      }

      onClick={
        onClick
      }

      style={{
        width: 36,
        height: 36,

        borderRadius:
          10,

        border:
          active
            ? '1px solid rgba(94,234,212,.62)'
            : PANEL_BORDER,

        color:
          active
            ? '#ffffff'
            : '#ccfbf1',

        background:
          active
            ? 'linear-gradient(145deg,rgba(20,184,166,.66),rgba(34,211,238,.34))'
            : 'linear-gradient(145deg,rgba(8,145,178,.28),rgba(2,6,23,.64))',

        boxShadow:
          active
            ? '0 0 24px rgba(45,212,191,.28)'
            : 'inset 0 1px 0 rgba(255,255,255,.10)',

        cursor:
          'pointer',

        fontWeight:
          900,

        fontSize:
          13,
      }}
    >
      {children}
    </button>
  );
}


/* =========================================================
   MINI STAT
========================================================= */

function MiniStat({
  label,
  value,
}) {
  return (
    <div
      style={{
        minWidth:
          0,

        padding:
          '5px 5px',

        borderRadius:
          10,

        background:
          'rgba(2,10,20,.35)',

        border:
          '1px solid rgba(103,232,249,.12)',

        textAlign:
          'center',
      }}
    >
      <div
        style={{
          color:
            '#70889a',

          fontSize:
            6.8,

          fontWeight:
            900,

          letterSpacing:
            '.35px',

          textTransform:
            'uppercase',

          whiteSpace:
            'nowrap',
        }}
      >
        {label}
      </div>


      <div
        style={{
          color:
            '#e7f8ff',

          marginTop:
            1,

          fontSize:
            15,

          lineHeight:
            1.05,

          fontWeight:
            950,
        }}
      >
        {value}
      </div>
    </div>
  );
}


/* =========================================================
   TYPE PILL
========================================================= */

function TypePill({
  type,
  count,
}) {
  const meta =
    RACK_TYPE_META[
      type
    ];


  if (
    !meta
  ) {
    return null;
  }


  return (
    <div
      style={{
        minHeight:
          20,

        padding:
          '2px 6px',

        borderRadius:
          999,

        display:
          'inline-flex',

        alignItems:
          'center',

        gap:
          4,

        border:
          `1px solid ${meta.color}33`,

        background:
          'rgba(2,10,20,.38)',

        whiteSpace:
          'nowrap',
      }}
    >
      <span
        style={{
          width:
            6,

          height:
            6,

          flex:
            '0 0 auto',

          borderRadius:
            '50%',

          background:
            meta.color,

          boxShadow:
            `0 0 7px ${meta.color}77`,
        }}
      />


      <span
        style={{
          color:
            '#b9ccda',

          fontSize:
            7.5,

          lineHeight:
            1,

          fontWeight:
            800,
        }}
      >
        {RACK_DISPLAY_LABELS[
          type
        ]}
      </span>


      {count !==
        undefined && (
        <strong
          style={{
            color:
              meta.accent,

            fontSize:
              7.8,

            lineHeight:
              1,

            fontWeight:
              950,
          }}
        >
          {count}
        </strong>
      )}
    </div>
  );
}


/* =========================================================
   RACK HOVER CARD
========================================================= */

function RackHoverCard({
  rack,
}) {
  if (
    !rack?.occupied
  ) {
    return null;
  }


  const meta =
    RACK_TYPE_META[
      rack.type
    ];


  const position =
    rack.architecturalFqn
      ? rack.architecturalFqn

      : `R${rack.row}-${String(
          rack.p2pPosition ||
            ''
        ).padStart(
          2,
          '0'
        )}`;


  return (
    <div
      className="s6-hover-card"

      style={{
        ...summaryStyle,

        position:
          'absolute',

        zIndex:
          28,

        top:
          14,

        right:
          14,

        width:
          'clamp(300px,25vw,360px)',

        borderRadius:
          16,

        padding:
          '10px 12px',

        pointerEvents:
          'none',
      }}
    >
      <div
        style={{
          color:
            '#5eead4',

          fontSize:
            8,

          fontWeight:
            900,

          letterSpacing:
            '.7px',

          textTransform:
            'uppercase',
        }}
      >
        Rack Details
      </div>


      <div
        style={{
          display:
            'flex',

          alignItems:
            'center',

          justifyContent:
            'space-between',

          gap:
            8,

          marginTop:
            5,
        }}
      >
        <div
          style={{
            fontSize:
              14,

            fontWeight:
              950,

            color:
              '#f8fafc',
          }}
        >
          {rack.id}
        </div>


        <TypePill
          type={
            rack.type
          }
        />
      </div>


      <div
        style={{
          marginTop:
            7,

          display:
            'grid',

          gridTemplateColumns:
            '1fr 1fr',

          gap:
            6,

          color:
            '#8fa9ba',

          fontSize:
            8.5,
        }}
      >
        <div>
          <span
            style={{
              color:
                '#657f92',
            }}
          >
            Hall
          </span>

          <br />

          <b
            style={{
              color:
                '#d5e7f2',
            }}
          >
            {rack.hall}
          </b>
        </div>


        <div>
          <span
            style={{
              color:
                '#657f92',
            }}
          >
            Position
          </span>

          <br />

          <b
            style={{
              color:
                meta.accent,
            }}
          >
            {position}
          </b>
        </div>
      </div>


      <div
        style={{
          marginTop:
            7,

          color:
            '#70899b',

          fontSize:
            8,

          fontWeight:
            700,
        }}
      >
        Click rack for elevation and BOM details
      </div>
    </div>
  );
}


/* =========================================================
   RACK ELEVATION PANEL
========================================================= */

function RackElevationPanel({
  rack,
  onClose,
}) {
  if (
    !rack?.occupied
  ) {
    return null;
  }


  const meta =
    RACK_TYPE_META[
      rack.type
    ];


  return (
    <div
      className="s6-rack-panel"

      style={{
        ...summaryStyle,

        position:
          'absolute',

        zIndex:
          30,

        right:
          14,

        top:
          14,

        width:
          'clamp(320px,28vw,400px)',

        maxHeight:
          'calc(100% - 85px)',

        overflowY:
          'auto',

        borderRadius:
          18,

        padding:
          14,
      }}
    >
      <div
        style={{
          display:
            'flex',

          alignItems:
            'flex-start',

          justifyContent:
            'space-between',

          gap:
            10,
        }}
      >
        <div>
          <div
            style={{
              color:
                meta.accent,

              fontSize:
                8,

              fontWeight:
                950,

              textTransform:
                'uppercase',

              letterSpacing:
                '.8px',
            }}
          >
            Rack Elevation
          </div>


          <div
            style={{
              fontSize:
                16,

              fontWeight:
                950,

              marginTop:
                3,
            }}
          >
            {rack.id}
          </div>


          <div
            style={{
              color:
                '#9ab3c4',

              fontSize:
                9.5,

              marginTop:
                2,
            }}
          >
            {rack.hall}
            {' • '}
            Row {rack.row}
            {' • '}
            {RACK_DISPLAY_LABELS[
              rack.type
            ]}
          </div>
        </div>


        <button
          type="button"

          onClick={
            onClose
          }

          style={{
            border:
              0,

            background:
              'transparent',

            color:
              '#94a3b8',

            fontSize:
              20,

            cursor:
              'pointer',
          }}
        >
          ×
        </button>
      </div>


      <div
        style={{
          marginTop:
            12,

          display:
            'grid',

          gridTemplateColumns:
            '90px 1fr',

          gap:
            10,
        }}
      >
        <div
          style={{
            border:
              `1px solid ${meta.color}66`,

            borderRadius:
              9,

            padding:
              '5px 6px',

            background:
              '#050b11',

            minHeight:
              242,

            display:
              'flex',

            flexDirection:
              'column-reverse',

            gap:
              2,
          }}
        >
          {meta.elevation.map(
            (
              block,
              index
            ) => (
              <div
                key={
                  index
                }

                style={{
                  flex:
                    Math.max(
                      1,
                      block.size
                    ),

                  minHeight:
                    18,

                  background:
                    `${meta.color}${
                      index %
                      2
                        ? '4d'
                        : '2f'
                    }`,

                  border:
                    `1px solid ${meta.color}77`,

                  borderRadius:
                    3,

                  color:
                    '#eafcff',

                  fontSize:
                    7.3,

                  fontWeight:
                    800,

                  display:
                    'grid',

                  placeItems:
                    'center',

                  textAlign:
                    'center',

                  padding:
                    2,
                }}
              >
                {block.label}
              </div>
            )
          )}
        </div>


        <div>
          <div
            style={{
              color:
                '#6f879a',

              fontSize:
                8.5,

              fontWeight:
                900,

              textTransform:
                'uppercase',
            }}
          >
            Cabinet / Source
          </div>


          <div
            style={{
              color:
                '#d5e7f2',

              fontSize:
                10.5,

              lineHeight:
                1.35,

              marginTop:
                3,
            }}
          >
            {meta.cabinet}
          </div>


          <div
            style={{
              color:
                '#67e8f9',

              fontSize:
                9,

              lineHeight:
                1.35,

              marginTop:
                5,
            }}
          >
            {meta.elevationSource}
          </div>


          {rack.architecturalFqn && (
            <div
              style={{
                marginTop:
                  8,

                color:
                  '#b9ccda',

                fontSize:
                  9.5,
              }}
            >
              Architectural FQN:{' '}

              <b>
                {rack.architecturalFqn}
              </b>
            </div>
          )}


          {rack.p2pPosition && (
            <div
              style={{
                marginTop:
                  3,

                color:
                  '#b9ccda',

                fontSize:
                  9.5,
              }}
            >
              P2P row position:{' '}

              <b>
                {rack.row}
                -
                {rack.p2pPosition}
              </b>
            </div>
          )}
        </div>
      </div>


      <div
        style={{
          height:
            1,

          background:
            'rgba(103,232,249,.16)',

          margin:
            '13px 0 10px',
        }}
      />


      <div
        style={{
          color:
            '#5eead4',

          fontSize:
            9,

          fontWeight:
            950,

          textTransform:
            'uppercase',

          letterSpacing:
            '.7px',
        }}
      >
        Relevant BOM / Product IDs
      </div>


      <div
        style={{
          display:
            'grid',

          gap:
            5,

          marginTop:
            8,
        }}
      >
        {meta.equipment.map(
          (
            item,
            index
          ) => (
            <div
              key={
                index
              }

              style={{
                border:
                  '1px solid rgba(103,232,249,.12)',

                background:
                  'rgba(2,6,23,.32)',

                borderRadius:
                  8,

                padding:
                  '7px 8px',
              }}
            >
              <div
                style={{
                  fontSize:
                    10,

                  fontWeight:
                    850,

                  color:
                    '#e7f8ff',
                }}
              >
                {item.role}
              </div>


              <div
                style={{
                  fontSize:
                    9.5,

                  fontWeight:
                    900,

                  color:
                    meta.accent,

                  marginTop:
                    2,
                }}
              >
                {item.productId}

                {item.model
                  ? ` • ${item.model}`
                  : ''}
              </div>


              {item.note && (
                <div
                  style={{
                    fontSize:
                      8.6,

                    lineHeight:
                      1.35,

                    color:
                      '#8199ab',

                    marginTop:
                      3,
                  }}
                >
                  {item.note}
                </div>
              )}
            </div>
          )
        )}
      </div>


      <div
        style={{
          marginTop:
            10,

          fontSize:
            8.5,

          lineHeight:
            1.4,

          color:
            '#70889a',
        }}
      >
        Design/BOM reference data • Live telemetry not connected.
      </div>
    </div>
  );
}


/* =========================================================
   MAIN COMPONENT
========================================================= */

export default function SharonS6Scene() {
  const rootRef =
    useRef(null);


  const controlsRef =
    useRef(null);


  const cancelCameraAnimationRef =
    useRef(false);


  const [
    levelMode,
    setLevelMode,
  ] =
    useState(
      'stacked'
    );


  const [
    topView,
    setTopView,
  ] =
    useState(
      false
    );


  const [
    fullscreen,
    setFullscreen,
  ] =
    useState(
      false
    );


  const [
    showIntro,
    setShowIntro,
  ] =
    useState(
      true
    );


  const [
    hoveredRack,
    setHoveredRack,
  ] =
    useState(
      null
    );


  const [
    selectedRack,
    setSelectedRack,
  ] =
    useState(
      null
    );

  const [
  showRackPanel,
  setShowRackPanel,
] =
  useState(
    false
  );

  const [
  isSidebarHidden,
  setIsSidebarHidden,
] = useState(false);
  
  const [
    isTouring,
    setIsTouring,
  ] =
    useState(
      false
    );


    /* =====================================================
   DELAY RACK PANEL UNTIL CAMERA TRANSITION STARTS
===================================================== */

useEffect(
  () => {
    /*
     * No selected rack:
     * panel must stay closed.
     */
    if (
      !selectedRack
    ) {
      setShowRackPanel(
        false
      );

      return;
    }


    /*
     * A different rack was selected.
     *
     * Hide the panel first so the
     * camera transition gets a clean
     * rendering window.
     */
    setShowRackPanel(
      false
    );


    /*
     * Same delay style used by
     * the OG twin.
     */
    const timer =
      setTimeout(
        () => {
          setShowRackPanel(
            true
          );
        },

        600
      );


    return () => {
      clearTimeout(
        timer
      );
    };
  },

  [
    selectedRack,
  ]
);

  /* =====================================================
     FULLSCREEN STATE
  ===================================================== */

  useEffect(
    () => {
      const onFullscreen =
        () => {
          setFullscreen(
            document.fullscreenElement ===
              rootRef.current
          );
        };


      document.addEventListener(
        'fullscreenchange',
        onFullscreen
      );


      return () => {
        document.removeEventListener(
          'fullscreenchange',
          onFullscreen
        );
      };
    },

    []
  );


  const toggleFullscreen =
    useCallback(
      async () => {
        if (
          !document.fullscreenElement
        ) {
          await rootRef.current
            ?.requestFullscreen?.();
        } else {
          await document
            .exitFullscreen?.();
        }
      },

      []
    );

    /* =====================================================
   AUTO ROAM
===================================================== */

const toggleAutoTour =
  useCallback(
    () => {
      if (
        isTouring
      ) {
        /*
         * Stop tour.
         * Camera stays exactly
         * where the tour stopped.
         */
        setIsTouring(
          false
        );

        return;
      }


      /*
       * Starting the tour should
       * clear anything that could
       * fight for camera control.
       */
      setSelectedRack(
        null
      );

      setHoveredRack(
        null
      );

      setTopView(
        false
      );


      cancelCameraAnimationRef.current =
        true;


      setIsTouring(
        true
      );
    },

    [
      isTouring,
    ]
  );


  /* =====================================================
   T / F / R KEYBOARD SHORTCUTS
===================================================== */

useEffect(
  () => {
    const handleKey =
      (
        event
      ) => {
        const tag =
          event.target
            ?.tagName;


        if (
          [
            'INPUT',
            'TEXTAREA',
            'SELECT',
          ].includes(
            tag
          )
        ) {
          return;
        }


        if (
          event.repeat ||
          event.ctrlKey ||
          event.metaKey ||
          event.altKey
        ) {
          return;
        }


        const key =
          event.key.toLowerCase();


        /*
         * R = AUTO ROAM
         */
        if (
          key ===
          'r'
        ) {
          event.preventDefault();

          toggleAutoTour();

          return;
        }


        /*
         * T = TOP VIEW
         *
         * Top View stops
         * Auto Roam first.
         */
        if (
          key ===
          't'
        ) {
          event.preventDefault();


          setIsTouring(
            false
          );


          setSelectedRack(
            null
          );


          cancelCameraAnimationRef.current =
            false;


          setTopView(
            (
              current
            ) =>
              !current
          );


          return;
        }


        /*
         * F = FULLSCREEN
         */
        if (
          key ===
          'f'
        ) {
          event.preventDefault();

          toggleFullscreen();
        }
      };


    window.addEventListener(
      'keydown',
      handleKey
    );


    return () => {
      window.removeEventListener(
        'keydown',
        handleKey
      );
    };
  },

  [
    toggleFullscreen,
    toggleAutoTour,
  ]
);


  /* =====================================================
     LEVEL CHANGE
  ===================================================== */

  const changeLevel = (id) => {
  setIsTouring(false);

  cancelCameraAnimationRef.current =
    false;

  setLevelMode(
    id
  );

  setTopView(
    false
  );

  setSelectedRack(
    null
  );

  setHoveredRack(
    null
  );
};


  /* =====================================================
     RENDER
  ===================================================== */

  return (
    <div
      ref={
        rootRef
      }

      className="s6-twin-root"
    >
      {showIntro && (
        <Intro
          onDone={() =>
            setShowIntro(
              false
            )
          }
        />
      )}


      {/* =================================================
          STYLES
      ================================================= */}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@500;600;700;800;900&display=swap');

        .s6-twin-root {
          position: relative;

          width: 100%;
          height: 100%;
          min-height: 0;

          overflow: hidden;

          isolation: isolate;

          color: #f8fafc;

          background:
            radial-gradient(
              circle at 72% 18%,
              rgba(45,212,191,.16),
              transparent 34%
            ),
            radial-gradient(
              circle at 18% 82%,
              rgba(34,211,238,.13),
              transparent 36%
            ),
            ${DARK};

          font-family:
            "Montserrat",
            "Segoe UI",
            sans-serif;

          font-weight: 500;
        }


        .s6-twin-root *,
        .s6-twin-root *::before,
        .s6-twin-root *::after {
          box-sizing: border-box;
        }


        .s6-twin-root:fullscreen {
          height: 100dvh;
        }


        .s6-intro {
          position: absolute;
          inset: 0;

          z-index: 100;

          display: flex;

          flex-direction: column;

          align-items: center;

          justify-content: center;

          background:
            radial-gradient(
              circle at 50% 42%,
              rgba(45,212,191,.24),
              transparent 34%
            ),
            linear-gradient(
              135deg,
              #020b12,
              #06202a 52%,
              #020617
            );

          animation:
            s6IntroLeave
            1.5s
            cubic-bezier(.16,1,.3,1)
            both;
        }


        .s6-intro-mark {
          width: 108px;
          height: 108px;

          display: grid;

          place-items: center;

          border-radius: 26px;

          font-size: 42px;

          font-weight: 900;

          color: #ccfbf1;

          border:
            1px solid rgba(103,232,249,.4);

          background:
            linear-gradient(
              145deg,
              rgba(8,47,73,.72),
              rgba(2,6,23,.86)
            );

          box-shadow:
            0 0 54px rgba(34,211,238,.26),
            inset 0 1px 0 rgba(255,255,255,.14);
        }


        .s6-intro-title {
          margin-top: 18px;

          font-size:
            clamp(28px,4vw,46px);

          font-weight: 900;
        }


        .s6-intro-subtitle {
          margin-top: 7px;

          color: #a5f3fc;

          font-size: 12px;

          font-weight: 700;

          letter-spacing: .7px;

          text-transform: uppercase;
        }


        @keyframes s6IntroLeave {
          0%,
          62% {
            opacity: 1;
            transform: scale(1);
          }

          100% {
            opacity: 0;

            transform:
              scale(1.1);

            visibility:
              hidden;
          }
        }


        @keyframes s6HoverIn {
          from {
            opacity: 0;

            transform:
              translateY(-5px)
              scale(.985);
          }

          to {
            opacity: 1;

            transform:
              translateY(0)
              scale(1);
          }
        }


        .s6-hover-card {
          animation:
            s6HoverIn
            .15s
            ease-out
            both;
        }


        @keyframes s6RackPanelIn {
          from {
            opacity: 0;

            transform:
              translateX(24px)
              scale(.985);

            filter:
              blur(3px);
          }

          to {
            opacity: 1;

            transform:
              translateX(0)
              scale(1);

            filter:
              blur(0);
          }
        }


        .s6-rack-panel {
          animation:
            s6RackPanelIn
            .32s
            cubic-bezier(.16,1,.3,1)
            both;

          transform-origin:
            right center;
        }


        .s6-rack-panel::-webkit-scrollbar,
        .s6-sidebar::-webkit-scrollbar {
          width: 5px;
        }


        .s6-rack-panel::-webkit-scrollbar-thumb,
        .s6-sidebar::-webkit-scrollbar-thumb {
          background:
            rgba(103,232,249,.18);

          border-radius:
            999px;
        }


        .s6-sidebar-toggle:hover {
          background:
            linear-gradient(
              145deg,
              rgba(13,55,68,.98),
              rgba(3,17,29,.96)
            ) !important;

          color: #ffffff !important;
        }


        @media (max-width: 1200px) {
          .s6-sidebar-wrap {
            width:
              300px !important;
          }

          .s6-rack-panel,
          .s6-hover-card {
            width:
              320px !important;
          }
        }


        @media (max-width: 950px) {
          .s6-sidebar-wrap {
            width:
              280px !important;
          }

          .s6-rack-panel,
          .s6-hover-card {
            width:
              292px !important;
          }
        }
      `}</style>


      {/* =================================================
          LEFT SIDEBAR + ATTACHED COLLAPSE BUTTON
      ================================================= */}

      <div
        className="s6-sidebar-wrap"

        style={{
          position:
            'absolute',

          zIndex:
            40,

          top:
            14,

          left:
            14,

          width:
            'clamp(300px,24vw,340px)',

          transform:
            isSidebarHidden
              ? 'translateX(calc(-100% + 18px))'
              : 'translateX(0)',

          transition:
            'transform .32s cubic-bezier(.16,1,.3,1)',
        }}
      >
        <aside
          className="s6-sidebar"

          style={{
            ...summaryStyle,

            width:
              '100%',

            maxHeight:
              'calc(100vh - 28px)',

            overflowY:
              'auto',

            borderRadius:
              22,

            padding:
              16,

            opacity:
              isSidebarHidden
                ? 0
                : 1,

            pointerEvents:
              isSidebarHidden
                ? 'none'
                : 'auto',

            transition:
              'opacity .20s ease',
          }}
        >
          {/* HEADER */}

          <h1
            style={{
              margin:
                '0 0 4px',

              color:
                '#f8fafc',

              fontSize:
                25,

              lineHeight:
                1.05,

              fontWeight:
                900,

              letterSpacing:
                '-1px',
            }}
          >
            Sharon S6
          </h1>


          <div
            style={{
              color:
                '#8ba5b7',

              fontSize:
                9.5,

              fontWeight:
                600,
            }}
          >
            111 racks • Level 1 DH102 • Level 2 DH202
          </div>


          <div
            style={{
              height:
                1,

              background:
                'rgba(103,232,249,.18)',

              margin:
                '14px 0 12px',
            }}
          />


          {/* VIEW */}

          <div
            style={{
              fontSize:
                8,

              color:
                '#8fa9ba',

              fontWeight:
                900,

              letterSpacing:
                '.7px',

              textTransform:
                'uppercase',

              marginBottom:
                8,
            }}
          >
            View
          </div>


          <div
            style={{
              display:
                'grid',

              gridTemplateColumns:
                'repeat(3,minmax(0,1fr))',

              gap:
                7,
            }}
          >
            {SHARON_LEVEL_OPTIONS.map(
              (
                option
              ) => {
                const active =
                  option.id ===
                  levelMode;


                const labels =
                  LEVEL_TILE_LABELS[
                    option.id
                  ];


                return (
                  <button
                    key={
                      option.id
                    }

                    type="button"

                    title={
                      option.label
                    }

                    onClick={() =>
                      changeLevel(
                        option.id
                      )
                    }

                    style={{
                      minWidth:
                        0,

                      minHeight:
                        68,

                      padding:
                        '6px 4px',

                      borderRadius:
                        14,

                      border:
                        active
                          ? '1px solid rgba(94,234,212,.72)'
                          : '1px solid rgba(103,232,249,.13)',

                      background:
                        active
                          ? 'linear-gradient(145deg,rgba(20,184,166,.30),rgba(34,211,238,.11))'
                          : 'rgba(2,8,18,.26)',

                      color:
                        active
                          ? '#ecfeff'
                          : '#90a9ba',

                      cursor:
                        'pointer',

                      display:
                        'flex',

                      flexDirection:
                        'column',

                      alignItems:
                        'center',

                      justifyContent:
                        'center',

                      gap:
                        3,

                      boxShadow:
                        active
                          ? 'inset 0 1px 0 rgba(255,255,255,.09),0 0 22px rgba(45,212,191,.08)'
                          : 'none',

                      transition:
                        'border .18s ease, background .18s ease, transform .18s ease',
                    }}
                  >
                    <span
                      style={{
                        fontSize:
                          13,

                        lineHeight:
                          1,

                        fontWeight:
                          900,
                      }}
                    >
                      {labels.title}
                    </span>


                    <span
                      style={{
                        color:
                          active
                            ? '#a7f3d0'
                            : '#607b8f',

                        fontSize:
                          7.3,

                        lineHeight:
                          1,

                        fontWeight:
                          800,
                      }}
                    >
                      {labels.subtitle}
                    </span>
                  </button>
                );
              }
            )}
          </div>


          {/* =================================================
              MERGED PROJECT SUMMARY
          ================================================= */}

          <div
            style={{
              marginTop:
                12,

              paddingTop:
                11,

              borderTop:
                '1px solid rgba(103,232,249,.12)',
            }}
          >
            <div
              style={{
                display:
                  'flex',

                alignItems:
                  'center',

                justifyContent:
                  'space-between',

                gap:
                  8,

                marginBottom:
                  7,
              }}
            >
              <span
                style={{
                  color:
                    '#5eead4',

                  fontSize:
                    7.5,

                  fontWeight:
                    900,

                  letterSpacing:
                    '.65px',

                  textTransform:
                    'uppercase',
                }}
              >
                Project Summary
              </span>


              <span
                style={{
                  color:
                    '#657f92',

                  fontSize:
                    7,

                  fontWeight:
                    700,
                }}
              >
                S6
              </span>
            </div>


            {/* SUMMARY COUNTS */}

            <div
              style={{
                display:
                  'grid',

                gridTemplateColumns:
                  'repeat(3,minmax(0,1fr))',

                gap:
                  4,
              }}
            >
              <MiniStat
                label="Total"

                value={
                  SHARON_RACK_COUNTS
                    .project
                    .total
                }
              />


              <MiniStat
                label="DH102"

                value={
                  SHARON_RACK_COUNTS
                    .level1
                    .total
                }
              />


              <MiniStat
                label="DH202"

                value={
                  SHARON_RACK_COUNTS
                    .level2
                    .total
                }
              />
            </div>


            {/* RACK TYPE STATUS */}

            <div
              style={{
                marginTop:
                  9,

                marginBottom:
                  6,

                color:
                  '#71899a',

                fontSize:
                  7.3,

                fontWeight:
                  900,

                letterSpacing:
                  '.6px',

                textTransform:
                  'uppercase',
              }}
            >
              Rack Types
            </div>


            <div
              style={{
                display:
                  'flex',

                flexWrap:
                  'wrap',

                columnGap:
                  4,

                rowGap:
                  4,
              }}
            >
              {RACK_TYPE_ORDER.map(
                (
                  type
                ) => (
                  <TypePill
                    key={
                      type
                    }

                    type={
                      type
                    }

                    count={
                      SHARON_RACK_COUNTS
                        .project[
                        type
                      ] ||
                      0
                    }
                  />
                )
              )}
            </div>
          </div>
        </aside>


        {/* =================================================
            OG-STYLE SIDEBAR ARROW
        ================================================= */}

        <button
          className="s6-sidebar-toggle"

          type="button"

          title={
            isSidebarHidden
              ? 'Show panel'
              : 'Hide panel'
          }

          aria-label={
            isSidebarHidden
              ? 'Show panel'
              : 'Hide panel'
          }

          onClick={() =>
            setIsSidebarHidden(
              (
                current
              ) =>
                !current
            )
          }

          style={{
            position:
              'absolute',

            zIndex:
              45,

            right:
              -14,

            top:
              '50%',

            transform:
              'translateY(-50%)',

            width:
              28,

            height:
              52,

            padding:
              0,

            borderRadius:
              11,

            border:
              '1px solid rgba(103,232,249,.28)',

            background:
              'linear-gradient(145deg,rgba(6,24,38,.96),rgba(2,8,18,.88))',

            backdropFilter:
              'blur(18px)',

            WebkitBackdropFilter:
              'blur(18px)',

            color:
              '#a5f3fc',

            display:
              'grid',

            placeItems:
              'center',

            cursor:
              'pointer',

            boxShadow:
              '0 8px 28px rgba(0,0,0,.30),inset 0 1px 0 rgba(255,255,255,.10)',

            fontSize:
              19,

            lineHeight:
              1,

            fontWeight:
              700,

            transition:
              'background .18s ease,color .18s ease',
          }}
        >
          {isSidebarHidden
            ? '›'
            : '‹'}
        </button>
      </div>


      {/* =================================================
          HOVER CARD
      ================================================= */}

      {hoveredRack
        ?.occupied &&
        !selectedRack && (
          <RackHoverCard
            rack={
              hoveredRack
            }
          />
        )}


      {/* =================================================
          CLICKED RACK PANEL
      ================================================= */}

      <RackElevationPanel
  rack={
    showRackPanel
      ? selectedRack
      : null
  }

  onClose={() => {
    setShowRackPanel(
      false
    );

    setSelectedRack(
      null
    );
  }}
/>


      {/* =================================================
          BOTTOM-RIGHT CONTROLS
          ONLY T + F
      ================================================= */}

      <div
        style={{
          ...summaryStyle,

          position:
            'absolute',

          zIndex:
            32,

          right:
            14,

          bottom:
            14,

          display:
            'grid',

          gridTemplateColumns:
            'repeat(3,36px)',

          gap:
            5,

          padding:
            5,

          borderRadius:
            11,
        }}
      >
        <GlassButton
          active={
            topView
          }

          title="Top View (T)"

          onClick={() => {
            setSelectedRack(
              null
            );

            cancelCameraAnimationRef.current =
              false;

            setTopView(
              (
                current
              ) =>
                !current
            );
          }}
        >
          T
        </GlassButton>
        
        <GlassButton
  active={
    isTouring
  }

  title={
    isTouring
      ? 'Stop Auto Roam (R)'
      : 'Start Auto Roam (R)'
  }

  onClick={
    toggleAutoTour
  }
>
  R
</GlassButton>

        <GlassButton
          active={
            fullscreen
          }

          title="Fullscreen (F)"

          onClick={
            toggleFullscreen
          }
        >
          F
        </GlassButton>
      </div>


      {/* =================================================
          THREE.JS CANVAS
      ================================================= */}

      <Canvas
        camera={{
          position: [
            0,
            12,
            108,
          ],

          fov:
            44,

          near:
            0.1,

          far:
            300,
        }}

        dpr={1}

        performance={{
          min:
            0.8,
        }}

        gl={{
          antialias:
            true,

          alpha:
            false,

          stencil:
            false,

          depth:
            true,

          powerPreference:
            'high-performance',
        }}

        style={{
          width:
            '100%',

          height:
            '100%',

          touchAction:
            'none',
        }}

        onPointerMissed={() => {
          setSelectedRack(
            null
          );

          setHoveredRack(
            null
          );
        }}
      >

        <Scene
          levelMode={
            levelMode
          }

          topView={
            topView
          }

          controlsRef={
            controlsRef
          }

          selectedRack={
            selectedRack
          }

          onRackSelect={(rack) => {
  /*
   * Same sequence as OG twin:
   *
   * 1. stop tour
   * 2. leave top view
   * 3. clear hover
   * 4. select rack
   *
   * RackFocusAnimator takes over
   * on the following render.
   */
  setIsTouring(false);

  setTopView(false);

  setHoveredRack(null);

  setSelectedRack(
    rack
  );
}}

          onRackHover={
            setHoveredRack
          }

          cancelAnimationRef={
            cancelCameraAnimationRef
          }

          isTouring={
            isTouring
          }
        />
      </Canvas>
    </div>
  );
}